import React, { useState, useMemo, useEffect } from "react";
import { useTable, Column, Row, Cell } from "react-table";
import { InformationCircleIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import { authenticatedFetch } from "./utils/authErrorHandler";
import { openDocumentPreview } from "./DocumentPreviewSlice_Store";
import { setLayoutMode } from "./LayoutSlice_Store";
import { canPreviewDocument } from "./services/documentPreviewService";

// Deal type interface
// Note: Array fields (team, people, advisors, vendors) come as actual arrays from both APIs
interface DealData {
  dealId: string;
  dealName: string;
  portfolioCompanyName?: string;
  year?: string | number;
  stage?: string;
  team?: string[];  // Array of team names e.g. ["SE Asia"]
  sector?: string;
  country?: string;
  description?: string;
  business?: string;
  fund?: string;
  companyOverview?: string;
  company_overview?: string;
  COMPANY_OVERVIEW?: string;
  inclusionRationale?: string;
  isPublished?: boolean;
  isSensitive?: boolean;
  // New fields from API
  investmentYear?: number;
  dealStage?: string;
  sensitivity?: string;
  dealType?: string;
  closingDate?: string | null;
  exitYear?: number | null;
  lastIcStage?: string | null;
  // EV/EBITDA Multiple fields
  entryEvEbitdaLtmActual?: number | null;
  entryEvEbitdaLtmBudget?: number | null;
  entryEvEbitdaLtmForecast?: number | null;
  exitEvEbitdaActual?: number | null;
  exitEvEbitdaBudget?: number | null;
  exitEvEbitdaForecast?: number | null;
  // Array fields
  vendor?: string[] | null;    // Array of vendor names
  vendors?: string[] | null;   // Array of vendor names
  people?: string[] | null;    // Array of people names with roles
  teamMembers?: string[] | null;  // Array of team member names
  advisors?: string[] | null;  // Array of advisor names with roles
  deadReason?: string | null;
  // eFront lifecycle & contact fields
  dead?: string | null;
  lastActiveStatus?: string | null;
  exitCompleted?: string | null;
  efrontIoContact1?: string | null;
  efrontIoContact2?: string | null;
  documents?: any[] | null;
  documentCount?: number;
}

// Document type interface
interface DocumentData {
  docId: string;
  dealId?: string;
  documentName?: string;
  originalFilename?: string;
  summaryText?: string;
  documentCategory?: string;
  mimeType?: string;
  fileSize?: number;
  processingStatus?: string;
  createdAt?: string;
  updatedAt?: string;
  documentOrigin?: string;
  filePath?: string;
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
}

/**
 * Extract docId from document (now standardized to camelCase)
 */
const getDocumentIdentifier = (document: Partial<DocumentData> = {}): string | null => {
  const docId = document.docId;

  if (!docId) {
    return null;
  }

  return docId;
};

// === Modal for detailed deal view ===
interface DealDetailsModalProps {
  deal: DealData | null;
  onClose: () => void;
}

function DealDetailsModal({ deal, onClose }: DealDetailsModalProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const [fullDealData, setFullDealData] = useState<DealData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'General' | 'Documents'>('General');

  // Get documents from Redux and filter by dealId
  const allDocuments = useAppSelector((state) => state.resultsSlice.documents || []) as DocumentData[];
  const dealDocuments = useMemo(() => {
    if (!deal?.dealId) return [];
    return allDocuments.filter(doc => doc.dealId === deal.dealId);
  }, [allDocuments, deal?.dealId]);

  // Fetch full deal details when deal changes
  useEffect(() => {
    if (!deal || !deal.dealId) return;

    setFullDealData(null);
    setLoading(true);
    setError(null);

    const fetchDealDetails = async () => {

      try {
        // Use the configured deals API base URL from environment variables
        const baseUrl = import.meta.env.VITE_DEALS_API_BASE_URL;

        const response = await authenticatedFetch(`${baseUrl}/api/objects/deals/${deal.dealId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch deal details: ${response.status} ${response.statusText}`);
        }

        const dealDetails = await response.json();
        setFullDealData(dealDetails);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to fetch deal details');
      } finally {
        setLoading(false);
      }
    };

    fetchDealDetails();
  }, [deal]);

  if (!deal) return null;

  const handleModalContentClick = (e: React.MouseEvent) => e.stopPropagation();

  // Use full deal data if available, otherwise fall back to minimal deal data
  const displayData = fullDealData || deal;

  // Determine if deal is sensitive (SENSITIVITY column contains 'TRUE' or 'FALSE' as varchar, returned as lowercase)
  const isSensitive = displayData.sensitivity?.toLowerCase() === 'true';
  const normalizedStage = (displayData.dealStage || displayData.stage || '').toLowerCase();
  const isDead = normalizedStage === 'dead';
  const isExited = normalizedStage === 'exited';

  // Format array as comma-separated string
  const formatArray = (value: string[] | null | undefined): string => {
    if (!value || value.length === 0) return '--';
    return value.join(', ');
  };

  // Parse "NAME as ROLE" format
  const parseNameRole = (entry: string): { name: string; role: string } => {
    const asMatch = entry.match(/^(.+?)\s+as\s+(.+)$/i);
    if (asMatch) {
      return { name: asMatch[1].trim(), role: asMatch[2].trim() };
    }
    return { name: entry, role: '' };
  };

  // Render people/advisors as a table
  const renderPeopleTable = (items: string[] | null | undefined): JSX.Element => {
    if (!items || items.length === 0) {
      return <span className="text-gray-500">--</span>;
    }

    const parsed = items.map(parseNameRole);
    const hasRoles = parsed.some(p => p.role);

    if (!hasRoles) {
      return <span>{items.join(', ')}</span>;
    }

    return (
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-1 pr-4 font-medium text-gray-600">Name</th>
            <th className="text-left py-1 font-medium text-gray-600">Role</th>
          </tr>
        </thead>
        <tbody>
          {parsed.map((item, idx) => (
            <tr key={idx} className="border-b border-gray-100 last:border-0">
              <td className="py-1 pr-4">{item.name}</td>
              <td className="py-1 text-gray-600">{item.role || '--'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-[90vh] flex flex-col"
        onClick={handleModalContentClick}
      >
        <div className="flex justify-between items-center p-5 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">
            {displayData.dealName || 'Loading...'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition-colors rounded-full p-1 pt-2"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        {/* Tab Navigation */}
        <div className="flex-shrink-0 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('General')}
            className={`relative inline-flex items-center py-2 px-5 text-sm font-semibold focus:outline-none transition-colors duration-200 ease-in-out ${
              activeTab === 'General'
                ? "border-b-2 border-indigo-600 text-indigo-600"
                : "text-gray-500 hover:text-gray-800 border-b-2 border-transparent"
            }`}
          >
            General
          </button>
          <button
            onClick={() => setActiveTab('Documents')}
            className={`relative inline-flex items-center py-2 px-5 text-sm font-semibold focus:outline-none transition-colors duration-200 ease-in-out ${
              activeTab === 'Documents'
                ? "border-b-2 border-indigo-600 text-indigo-600"
                : "text-gray-500 hover:text-gray-800 border-b-2 border-transparent"
            }`}
          >
            Documents ({dealDocuments.length})
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading deal details...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-2">Failed to load deal details</div>
              <div className="text-sm text-gray-500">{error}</div>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-2 bg-cvc-blue text-white rounded hover:bg-cvc-blue/90 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : activeTab === 'General' ? (
            <div className="space-y-6">
              {/* Top Section - Company Name */}
              <div>
                <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                  Company Name
                </dt>
                <dd className="mt-1 text-lg text-gray-900 font-semibold">
                  {isSensitive ? 'Sensitive' : (displayData.portfolioCompanyName || '--')}
                </dd>
              </div>

              {/* Business Description */}
              <div>
                <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </dt>
                <dd className="mt-1 text-base text-gray-900">
                  {displayData.business || 'No description'}
                </dd>
              </div>

              {/* Key Deal Information Grid */}
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Sector
                  </dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {displayData.sector || '--'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Investment Year
                  </dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {displayData.investmentYear || displayData.year || '--'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Country
                  </dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {displayData.country || '--'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Stage
                  </dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {displayData.dealStage || displayData.stage || '--'}
                  </dd>
                </div>
                {displayData.closingDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Close Year
                    </dt>
                    <dd className="mt-1 text-base text-gray-900">
                      {displayData.closingDate.substring(0, 4)}
                    </dd>
                  </div>
                )}
                {displayData.exitYear && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Exit Year
                    </dt>
                    <dd className="mt-1 text-base text-gray-900">
                      {displayData.exitYear}
                    </dd>
                  </div>
                )}
                {displayData.lastIcStage && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Last IC Stage
                    </dt>
                    <dd className="mt-1 text-base text-gray-900">
                      {displayData.lastIcStage}
                    </dd>
                  </div>
                )}
                {(displayData.efrontIoContact1 || displayData.efrontIoContact2) && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Deal Contacts
                    </dt>
                    <dd className="mt-1 text-base text-gray-900">
                      {renderPeopleTable([displayData.efrontIoContact1, displayData.efrontIoContact2].filter(Boolean) as string[])}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Team
                  </dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {formatArray(displayData.team)}
                  </dd>
                </div>
                {displayData.deadReason && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Dead Reason
                    </dt>
                    <dd className="mt-1 text-base text-gray-900">
                      {displayData.deadReason}
                    </dd>
                  </div>
                )}
                {isDead && displayData.lastActiveStatus && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Last Active Stage
                    </dt>
                    <dd className="mt-1 text-base text-gray-900">
                      {displayData.lastActiveStatus}
                    </dd>
                  </div>
                )}
                {isExited && displayData.exitCompleted && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                      Exit Completed
                    </dt>
                    <dd className="mt-1 text-base text-gray-900">
                      {displayData.exitCompleted}
                    </dd>
                  </div>
                )}
              </dl>

              {/* More Details Button - Placeholder for future DealTracker link */}
              {/* Hidden for now - uncomment when DealTracker integration is ready
              <div>
                <button
                  disabled
                  className="px-4 py-2 bg-gray-300 text-gray-600 rounded cursor-not-allowed"
                  title="DealTracker integration coming soon"
                >
                  View in DealTracker
                </button>
              </div>
              */}

              {/* Divider */}
              <div className="border-t border-gray-200 my-6"></div>

              {/* Bottom Section - Additional Details */}
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Business Overview
                  </dt>
                  <dd className="mt-1 text-base text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {displayData.companyOverview || displayData.company_overview || displayData.COMPANY_OVERVIEW || '--'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {formatArray(displayData.vendor)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                    People
                  </dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {renderPeopleTable(displayData.people)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Advisors
                  </dt>
                  <dd className="mt-1 text-base text-gray-900">
                    {renderPeopleTable(displayData.advisors)}
                  </dd>
                </div>
              </dl>
            </div>
          ) : activeTab === 'Documents' ? (
            <div className="space-y-4">
              {dealDocuments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Document Name
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dealDocuments.map((document) => {
                        const documentName = document.originalFilename || document.documentName || 'Untitled Document';

                        return (
                          <tr
                            key={document.docId}
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => {
                              // Build document object for preview
                              const documentForPreview = {
                                originalFilename: document.originalFilename,
                                docId: document.docId,
                                filename: document.documentName,
                                summaryText: document.summaryText,
                                documentCategory: document.documentCategory,
                                mimeType: document.mimeType,
                                fileSize: document.fileSize,
                                processingStatus: document.processingStatus,
                                createdAt: document.createdAt,
                                updatedAt: document.updatedAt,
                                documentOrigin: document.documentOrigin,
                                filePath: document.filePath,
                                sharepointSiteId: document.sharepointSiteId,
                                sharepointDriveId: document.sharepointDriveId,
                                sharepointItemId: document.sharepointItemId
                              };

                              // Check if document can be previewed
                              if (canPreviewDocument(documentForPreview)) {
                                dispatch(openDocumentPreview(documentForPreview));
                                dispatch(setLayoutMode('workspace'));
                                onClose(); // Close the modal when opening document preview
                              }
                            }}
                          >
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                                {documentName}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No documents available for this deal.
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// === Modal for deal rationale ===
interface DealRationaleModalProps {
  deal: DealData | null;
  onClose: () => void;
}

function DealRationaleModal({ deal, onClose }: DealRationaleModalProps): JSX.Element | null {
  useEffect(() => {
    if (!deal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deal, onClose]);

  if (!deal) return null;

  const handleModalContentClick = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={handleModalContentClick}
      >
        <div className="flex justify-between items-center p-5 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">
            Inclusion Rationale: {deal.dealName || 'Deal'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition-colors rounded-full p-1 pt-2"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          <div className="text-base text-gray-700 leading-relaxed">
            {deal.inclusionRationale || 'No rationale available for this deal.'}
          </div>
        </div>
      </div>
    </div>
  );
}

// === Actions Cell Component ===
interface ActionsCellProps {
  deal: DealData;
  onShowRationale: (deal: DealData) => void;
}

function ActionsCell({ deal, onShowRationale }: ActionsCellProps): JSX.Element {
  const hasRationale = deal.inclusionRationale;

  if (!hasRationale) {
    return <></>;
  }

  return (
    <button
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onShowRationale(deal);
      }}
      className="text-gray-400 hover:text-cvc-blue transition-colors"
      aria-label="View rationale"
    >
      <InformationCircleIcon className="w-5 h-5" />
    </button>
  );
}

// === Main Table Component ===
export default function DealsTable_Component(): JSX.Element {
  const dispatch = useAppDispatch();
  const [selectedDeal, setSelectedDeal] = useState<DealData | null>(null);
  const [rationaleModalDeal, setRationaleModalDeal] = useState<DealData | null>(null);

  // Selectors for deals and documents from results slice
  const activeDeals = useAppSelector((state) => state.resultsSlice.deals || []) as DealData[];
  const status = useAppSelector((state) => state.resultsSlice.status);
  const availableDocs = useAppSelector((state) => state.resultsSlice.documents || []) as DocumentData[];

  const isLoading = status === "loading";

  // DEBUG: Log deals received by table component
  useEffect(() => {
    if (activeDeals.length > 0) {
      console.log("🔍 [DealsTable] Rendering deals, count:", activeDeals.length);
      const sample = activeDeals[0];
      console.log("🔍 [DealsTable] Sample deal for table:", {
        dealName: sample.dealName,
        year: sample.year,
        investmentYear: sample.investmentYear,
        stage: sample.stage,
        dealStage: sample.dealStage,
        team: sample.team,
        description: sample.description,
        allKeys: Object.keys(sample),
      });
    }
  }, [activeDeals]);

  const columns = useMemo<Column<DealData>[]>(
    () => [
      {
        Header: "",
        id: "actions",
        width: 40,
        Cell: ({ row }: { row: Row<DealData> }) => (
          <ActionsCell
            deal={row.original}
            onShowRationale={setRationaleModalDeal}
          />
        ),
      },
      {
        Header: "Deal Name",
        accessor: "dealName",
        Cell: ({ value, row }: { value: string | undefined; row: Row<DealData> }) => {
          const dealId = row.original.dealId;
          // Check multiple sources for document availability:
          // 1. documentCount field from backend (most reliable)
          // 2. documents array in Redux state
          // 3. embedded documents on the deal object
          const hasDocumentCount = (row.original.documentCount ?? 0) > 0;
          const hasDocumentsInRedux = availableDocs.some(doc => doc.dealId === dealId);
          const hasEmbeddedDocuments = (row.original.documents?.length ?? 0) > 0;
          const hasDocuments = hasDocumentCount || hasDocumentsInRedux || hasEmbeddedDocuments;
          const isPC = row.original.dealType === "PC";
          return (
            <div className="flex items-center gap-2">
              <span className={`${isPC ? "text-cvc-pc bg-cvc-pcBg" : "text-cvc-blue bg-cvc-linkBg"} rounded-[3px] px-1`}>{value || "--"}</span>
              {hasDocuments && (
                <DocumentTextIcon
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  title="Has documents"
                />
              )}
            </div>
          );
        }
      },
      {
        Header: "Type",
        id: "dealType",
        width: 60,
        Cell: ({ row }: { row: Row<DealData> }) => {
          const isPC = row.original.dealType === "PC";
          return (
            <span className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-semibold rounded-full ${isPC ? "bg-cvc-pcBg text-cvc-pc" : "bg-cvc-linkBg text-cvc-blue"}`}>
              {isPC ? "Credit" : "PE"}
            </span>
          );
        }
      },
      {
        Header: "Company",
        accessor: "portfolioCompanyName",
        Cell: ({ row }: { row: Row<DealData> }) => {
          const deal = row.original;
          if (deal.sensitivity?.toLowerCase() === 'true') {
            return "Sensitive";
          }
          return deal.portfolioCompanyName || "--";
        }
      },
      {
        Header: "Description",
        accessor: "business",
        Cell: ({ value }: { value: string | undefined }) => {
          if (!value) return "No description";
          const maxLength = 200;
          return value.length > maxLength
            ? `${value.substring(0, maxLength)}...`
            : value;
        }
      },
      {
        Header: "Year",
        accessor: "year",
        Cell: ({ value }: { value: string | number | undefined }) => value || "--"
      },
      {
        Header: "Stage",
        accessor: "stage",
        Cell: ({ value }: { value: string | undefined }) => value || "--"
      },
      {
        Header: "Country",
        accessor: "country",
        Cell: ({ value }: { value: string | undefined }) => value || "--"
      },
    ],
    [availableDocs]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<DealData>({
      columns,
      data: activeDeals,
      getRowId: (row) => row.dealName,
    });

  return (
    <>
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 w-full min-w-0 border border-cvc-tableBorder rounded-[5px] overflow-y-auto overflow-x-auto">
          <table
            {...getTableProps()}
            className="min-w-full tablet:min-w-[900px] divide-y divide-cvc-tableBorder"
          >
            <thead className="bg-cvc-tableHeader sticky top-0 z-10">
              {headerGroups.map((headerGroup) => {
                const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={headerGroupKey} {...headerGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const { key: headerKey, ...headerProps } = column.getHeaderProps();
                      const isActionsColumn = column.id === "actions";
                      return (
                        <th
                          key={headerKey}
                          {...headerProps}
                          scope="col"
                          className={`py-3 text-left text-sm font-cvcBody font-normal text-cvc-headerText capitalize tracking-wider ${
                            isActionsColumn ? "px-2 w-8" : "px-3"
                          }`}
                        >
                          {column.render("Header") as React.ReactNode}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody
              {...getTableBodyProps()}
              className="bg-white divide-y divide-cvc-tableBorder font-cvcBody"
            >
              {isLoading ? (
                [...Array(10)].map((_, rowIndex) => (
                  <tr key={`skeleton-${rowIndex}`}>
                    {columns.map((_, colIndex) => (
                      <td
                        key={`skeleton-cell-${rowIndex}-${colIndex}`}
                        className="px-6 py-4"
                      >
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length > 0 ? (
                rows.map((row) => {
                  prepareRow(row);
                  const { key, ...rowProps } = row.getRowProps();
                  return (
                    <tr
                      key={key}
                      {...rowProps}
                      className="hover:bg-cvc-lb2 transition-colors duration-200 cursor-pointer"
                      onClick={() => setSelectedDeal(row.original)}
                    >
                      {row.cells.map((cell) => {
                        const { key: cellKey, ...cellProps } = cell.getCellProps();
                        const isDescriptionColumn = cell.column.id === "business";
                        const isActionsColumn = cell.column.id === "actions";
                        return (
                          <td
                            key={cellKey}
                            {...cellProps}
                            className={`py-4 text-sm text-cvc-text ${
                              isActionsColumn
                                ? "px-2 w-8"
                                : isDescriptionColumn
                                  ? "px-3"
                                  : "px-3 truncate max-w-48"
                            }`}
                          >
                            {cell.render("Cell") as React.ReactNode}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr key="empty-state-row">
                  <td
                    colSpan={columns.length}
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No deals to display.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <DealDetailsModal
        key={selectedDeal?.dealId ?? ''}
        deal={selectedDeal}
        onClose={() => setSelectedDeal(null)}
      />
      <DealRationaleModal
        deal={rationaleModalDeal}
        onClose={() => setRationaleModalDeal(null)}
      />
    </>
  );
}
